import { useState, useCallback, useRef } from 'react';
import { MessageSquarePlus, X, Upload, Check } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import FeedbackInterview from './FeedbackInterview';
import Button from './ui/Button';

const PAGE_MODULES = {
  '/': 'dashboard',
  '/leads': 'crm',
  '/accounts': 'crm',
  '/contacts': 'crm',
  '/projects': 'watchlist',
  '/proposals': 'proposals',
  '/kee': 'kee',
  '/roadmap': 'roadmap',
};

const CATEGORIES = ['bug', 'feature_request', 'usability', 'accuracy', 'performance', 'other'];
const SEVERITIES = ['low', 'medium', 'high', 'critical'];

function getToken() {
  return localStorage.getItem('mickey_token');
}

/**
 * FeedbackTab — floating "Give Feedback" tab anchored after the sidebar.
 * Three-step flow: (1) initial form, (2) AI interview, (3) done.
 */
export default function FeedbackTab() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('form'); // form | interview | done
  const [feedbackId, setFeedbackId] = useState(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('bug');
  const [severity, setSeverity] = useState('medium');
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);

  // Interview state — `questions` is the current turn's batch (one or more questions).
  const [questions, setQuestions] = useState([]);
  const [interviewLoading, setInterviewLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const module = PAGE_MODULES[location.pathname] || 'other';

  function reset() {
    setStep('form');
    setFeedbackId(null);
    setTitle('');
    setDescription('');
    setCategory('bug');
    setSeverity('medium');
    setScreenshotUrl(null);
    setScreenshotPreview(null);
    setQuestions([]);
    setError(null);
  }

  function close() {
    setOpen(false);
    setTimeout(reset, 300);
  }

  async function uploadScreenshot(file) {
    setUploadingScreenshot(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/feedback/upload-screenshot', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      // An expired session goes to sign-in, as api.js does; a non-JSON failure reports its status.
      if (res.status === 401) {
        localStorage.removeItem('mickey_token');
        window.location.href = '/login';
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
      setScreenshotUrl(data.url);
      setScreenshotPreview(URL.createObjectURL(file));
    } catch (e) {
      setError(e.message);
    } finally {
      setUploadingScreenshot(false);
    }
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (file) await uploadScreenshot(file);
    e.target.value = '';
  }

  async function submitInitial() {
    if (!title.trim()) { setError('Title is required.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post('/feedback/intake/start', {
        title: title.trim(),
        description: description.trim(),
        category,
        severity,
        module,
        area: location.pathname,
        screenshot_url: screenshotUrl,
      });
      setFeedbackId(res.feedback?.id ?? res.id);
      if (res.needs_more && res.questions?.length > 0) {
        setQuestions(res.questions);
        setStep('interview');
      } else {
        setStep('done');
      }
    } catch (e) {
      setError(e.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  // Each answer is posted immediately; the server's needs_more flag — not local index
  // math — decides whether the interview continues or finalizes.
  const handleAnswer = useCallback(async (q, a) => {
    setError(null);
    setInterviewLoading(true);
    try {
      const res = await api.post(`/feedback/intake/${feedbackId}/answer`, { answer: a });
      if (res.needs_more && res.questions?.length > 0) {
        setQuestions(res.questions);
      } else {
        await api.post(`/feedback/intake/${feedbackId}/finalize`, {});
        setStep('done');
      }
    } catch (e) {
      // Keep the user in the interview so they can retry — the feedback item is already
      // saved, but don't fake a successful submission or strand the answer.
      setError(e.message || 'Could not save your answer. Please try again.');
    } finally {
      setInterviewLoading(false);
    }
  }, [feedbackId]);

  const handleInterviewComplete = useCallback(async () => {
    if (feedbackId) {
      try { await api.post(`/feedback/intake/${feedbackId}/finalize`, {}); } catch {}
    }
    setStep('done');
  }, [feedbackId]);

  return (
    <>
      {/* Floating tab button */}
      <button
        onClick={() => { setOpen(true); reset(); }}
        className="fixed bottom-4 left-4 z-40 flex items-center gap-2 rounded-full bg-primary px-3 py-2 text-sm font-medium text-white shadow-lg transition-all hover:bg-primary/90 sm:bottom-6 sm:px-4 md:left-[224px]"
        title="Give Feedback"
        aria-label="Give Feedback"
      >
        <MessageSquarePlus size={16} />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {/* Panel backdrop + drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-start pointer-events-none">
          {/* Clickable backdrop */}
          <div
            className="absolute inset-0 bg-black/20 pointer-events-auto"
            onClick={close}
          />
          {/* Drawer */}
          <div
            style={{ maxWidth: '420px' }}
            className="relative pointer-events-auto w-full bg-white dark:bg-slate-900 rounded-t-xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col md:left-[224px]"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                {step === 'done' ? 'Feedback Submitted' : step === 'interview' ? 'A few quick questions' : 'Give Feedback'}
              </h3>
              <button onClick={close} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={18} />
              </button>
            </div>

            <div className="px-4 py-4 overflow-y-auto max-h-[70vh]">
              {step === 'form' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Title <span className="text-red-400">*</span></label>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="What's the issue or idea?"
                      className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Details</label>
                    <textarea
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="What were you doing? What did you expect?"
                      className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-2 text-sm"
                      >
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Severity</label>
                      <select
                        value={severity}
                        onChange={(e) => setSeverity(e.target.value)}
                        className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-2 text-sm"
                      >
                        {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Screenshot upload */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Screenshot (optional)</label>
                    {screenshotPreview ? (
                      <div className="relative inline-block">
                        <img src={screenshotPreview} alt="Screenshot" className="rounded border max-h-32 max-w-full object-contain" />
                        <button
                          onClick={() => { setScreenshotUrl(null); setScreenshotPreview(null); }}
                          className="absolute -top-2 -right-2 rounded-full bg-red-500 text-white w-5 h-5 flex items-center justify-center text-xs"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingScreenshot}
                        className="flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 dark:border-slate-600 text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors w-full justify-center"
                      >
                        <Upload size={14} />
                        {uploadingScreenshot ? 'Uploading…' : 'Upload screenshot'}
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                  </div>

                  {error && <p className="text-xs text-red-500">{error}</p>}

                  <div className="text-xs text-gray-400 bg-gray-50 dark:bg-slate-800 rounded p-2">
                    Page: <span className="font-mono">{location.pathname}</span> &rarr; <span className="font-medium">{module}</span> module
                  </div>

                  <Button className="w-full" onClick={submitInitial} disabled={submitting || !title.trim()}>
                    {submitting ? 'Submitting…' : 'Submit Feedback'}
                  </Button>
                </div>
              )}

              {step === 'interview' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Help us understand your feedback better with a few quick questions.
                  </p>
                  <FeedbackInterview
                    questions={questions}
                    onAnswer={handleAnswer}
                    loading={interviewLoading}
                  />
                  {error && <p className="text-xs text-red-500">{error}</p>}
                  <button
                    onClick={handleInterviewComplete}
                    className="text-xs text-gray-400 hover:text-gray-600 underline"
                  >
                    Skip remaining questions
                  </button>
                </div>
              )}

              {step === 'done' && (
                <div className="text-center py-6 space-y-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto">
                    <Check size={24} className="text-green-600 dark:text-green-400" />
                  </div>
                  <p className="font-medium text-gray-800 dark:text-gray-100">Thank you!</p>
                  <p className="text-sm text-gray-500">Your feedback has been submitted and the team will review it.</p>
                  <Button variant="secondary" size="sm" onClick={close}>Close</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
